<template>
    <v-card>
        <v-card-title>{{ $t('Lukukuittaukset') }}</v-card-title>

        <v-card-text>
            <!-- Loading Indicator -->

            <v-skeleton-loader v-if="loading" type="table-row-divider, table-row@6"></v-skeleton-loader>


            <!-- Simple Table -->
            <v-simple-table v-else-if="!loading && pages.length > 0">
                <template v-slot:default>
                    <thead>
                        <tr>
                            <th v-for="header in headers" :key="header.value" :style="{ width: header.width }">
                                {{ header.text }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="item in pages" :key="item.id" :class="getRowClass(item)">
                            <!-- Title Column -->
                            <td>
                                <div :class="{ [`ml-${item.level * 4}`]: item.level > 0 }" class="d-flex align-center">
                                    <v-icon v-if="item.isChild" small class="mr-2 text--secondary">
                                        {{ icons.mdiSubdirectoryArrowRight }}
                                    </v-icon>
                                    <span>{{ item.title }}</span>
                                </div>
                            </td>

                            <!-- Edit Column -->
                            <td>
                                <v-btn :to="{ name: 'admin-page-form', params: { id: item.id } }" icon x-small
                                    color="primary">
                                    <v-icon small>{{ icons.mdiPencil }}</v-icon>
                                </v-btn>
                            </td>

                            <!-- Version Column -->
                            <td>
                                <v-chip v-if="item.require_acknowledgment" color="primary" small>
                                    v{{ item.acknowledgment_version || 1 }}
                                </v-chip>
                            </td>

                            <!-- Acknowledgment Count Column -->
                            <td>
                                <div class="d-flex align-center" v-if="item.require_acknowledgment">
                                    <div style="width: 100px;">
                                        <span class="">{{ item.acknowledgment_count || 0 }}</span>
                                        /
                                        <span class="mr-2">{{ item.total_target_users || 0 }}</span>
                                    </div>
                                    <v-progress-linear :value="getAcknowledgmentPercentage(item)"
                                        :color="getProgressColor(getAcknowledgmentPercentage(item))" height="10" rounded
                                        style="min-width: 60px;"></v-progress-linear>
                                </div>
                            </td>

                            <!-- Acknowledgment Rate Column -->
                            <td>
                                <span v-if="item.require_acknowledgment">
                                    {{ getAcknowledgmentPercentage(item) }}%
                                </span>
                            </td>

                            <!-- Actions Column -->
                            <td>
                                <v-btn v-if="item.require_acknowledgment" @click="openStatsDialog(item)" color="primary"
                                    small>
                                    <v-icon small left>{{ icons.mdiListStatus }}</v-icon>
                                    {{ $t('Raportti') }}
                                </v-btn>
                            </td>
                        </tr>
                    </tbody>
                </template>
            </v-simple-table>

            <!-- No Data Message -->
            <div v-if="!loading && pages.length === 0" class="text-center pa-4 text--secondary">
                {{ $t('Lukukuittauksia ei ole käytössä') }}
            </div>

            <!-- Stats Dialog -->
            <v-dialog v-model="statsDialog" max-width="1200px" scrollable>
                <v-card style="min-height: 75vh;">
                    <v-card-text class="pa-0" v-if="selectedPageId">
                        <Acknowledgments :target-type="AcknowledgmentTypes.Page" :target-id="selectedPageId"
                            :version="selectedPageVersion" :title="acknowledgmentPageTitle"
                            :key="`ack-page-${selectedPageId}`">
                        </Acknowledgments>
                    </v-card-text>
                </v-card>
            </v-dialog>
        </v-card-text>
    </v-card>
</template>

<script>
import { computed, onMounted, ref, watch } from "@vue/composition-api";
import adminApi from "@/api/admin";
import {
    mdiListStatus,
    mdiClose,
    mdiSubdirectoryArrowRight,
    mdiPencil
} from "@mdi/js";
import { useTranslations } from "@/@core/libs/i18n";
import { useRouter } from "@core/utils";

import Acknowledgments from "@/components/acknowledgements/Acknowledgments.vue"
import AcknowledgmentTypes from "@/@core/utils/acknowledgment_types";
import { title } from "@/@core/utils/filter";

export default {
    components: {
        Acknowledgments
    },
    setup() {
        const pages = ref([]);
        const loading = ref(false);
        const statsDialog = ref(false);
        const selectedPageId = ref(null);
        const selectedPageVersion = ref(null);
        const acknowledgmentPageTitle = ref(null);

        const { t } = useTranslations();
        const { route } = useRouter();

        // Get current category from route query parameters
        const currentCategoryId = computed(() => {
            return route.value.query.category ? Number(route.value.query.category) : null;
        });

        const headers = ref([
            { text: t('Sivu'), value: 'title', sortable: true },
            { text: '', value: 'edit', sortable: false, width: '50px' },
            { text: t('Versio'), value: 'acknowledgment_version', sortable: true },
            { text: t('Lukukuittaukset'), value: 'acknowledgment_count', sortable: true },
            { text: '', value: 'acknowledgment_rate', sortable: false },
            { text: '', value: 'actions', sortable: false }
        ]);

        const fetchPages = async () => {
            loading.value = true;
            try {
                const response = await adminApi.getPageAcknowledgmentOverview(currentCategoryId.value);
                if (response.ok) {
                    pages.value = flattenPagesHierarchy(response.data.pages);
                } else {
                    console.error('Failed to load pages:', response.error);
                }
            } catch (error) {
                console.error('Error fetching pages:', error);
            } finally {
                loading.value = false;
            }
        };

        const flattenPagesHierarchy = (pages, level = 0) => {
            const flattened = [];

            pages.forEach(page => {
                // Transform the page data to match expected structure
                const transformedPage = {
                    ...page,
                    level,
                    isChild: level > 0,
                    // Map acknowledgment_stats to expected properties
                    acknowledgment_count: page.acknowledgment_stats?.acknowledged_count || 0,
                    pending_count: page.acknowledgment_stats?.pending_count || 0,
                    total_target_users: page.acknowledgment_stats?.total_target_users || 0,
                    total_users: page.acknowledgment_stats?.total_target_users || 0,
                    acknowledgment_rate: page.acknowledgment_stats?.acknowledgment_rate || 0
                };

                flattened.push(transformedPage);

                // Recursively add children with increased level
                if (page.children && page.children.length > 0) {
                    flattened.push(...flattenPagesHierarchy(page.children, level + 1));
                }
            });

            return flattened;
        };

        const getAcknowledgmentPercentage = (page) => {
            if (!page.require_acknowledgment || !page.total_users) return 0;
            return (((page.acknowledgment_count || 0) / page.total_users) * 100).toFixed(2);
        };

        const getProgressColor = (percentage) => {
            if (percentage >= 80) return 'success';
            if (percentage >= 50) return 'warning';
            return 'error';
        };

        const getRowClass = (item) => {
            const classes = [];
            if (item.level === 1) classes.push('row-level-1');
            if (item.level === 2) classes.push('row-level-2');
            if (item.level === 3) classes.push('row-level-3');
            return classes.join(' ');
        };

        const openStatsDialog = (page) => {
            selectedPageId.value = page.id;
            selectedPageVersion.value = page.acknowledgment_version;
            acknowledgmentPageTitle.value = page.title;
            statsDialog.value = true;
        };

        // Watch for category changes to refetch data
        watch(currentCategoryId, () => {
            fetchPages();
        });

        // Watch for dialog close to reset selected page
        watch(() => statsDialog.value, (newVal) => {
            if (!newVal) {
                selectedPageId.value = null;
            }
        });

        onMounted(() => {
            fetchPages();
        });

        return {
            pages,
            loading,
            statsDialog,
            selectedPageVersion,
            acknowledgmentPageTitle,
            selectedPageId,
            headers,
            getAcknowledgmentPercentage,
            AcknowledgmentTypes,
            getProgressColor,
            getRowClass,
            openStatsDialog,
            icons: {
                mdiListStatus,
                mdiClose,
                mdiSubdirectoryArrowRight,
                mdiPencil
            },
            t
        };
    }
};
</script>

<style scoped>
/* Table wrapper for horizontal scroll if needed */
.v-simple-table {
    overflow-x: auto;
}

/* Hierarchical indentation classes */
.ml-4 {
    margin-left: 16px !important;
}

.ml-8 {
    margin-left: 32px !important;
}

.ml-12 {
    margin-left: 48px !important;
}

.ml-16 {
    margin-left: 64px !important;
}

/* Child page styling */
.v-simple-table tbody tr td:first-child .d-flex.align-center {
    position: relative;
}

/* Add subtle background for child pages at different levels */
.v-simple-table tbody tr.row-level-1 {
    background-color: rgba(0, 0, 0, 0.02);
}

.v-simple-table tbody tr.row-level-2 {
    background-color: rgba(0, 0, 0, 0.04);
}

.v-simple-table tbody tr.row-level-3 {
    background-color: rgba(0, 0, 0, 0.06);
}

/* Table header styling */
.v-simple-table thead th {
    font-weight: 600;
}

/* Ensure proper spacing */
.v-simple-table td {
    padding: 8px 16px;
}
</style>
